import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { removeSupabaseChannelByName, subscribePostgresChannel } from "@/lib/realtime-channel";
import { bindRealtimeVisibilityPause } from "@/lib/realtime-visibility-pause";
import { REALTIME_PAUSE_ON_HIDDEN_ENABLED } from "@/lib/workspace-conversation-realtime-hub";
import { useToast } from "@/hooks/use-toast";
import type { RealtimeChannel } from "@supabase/supabase-js";
import {
  getFunnelDeletionBlockReason,
  getFunnelDeletionWarning,
} from "@/lib/funnel-deletion-protection";
import type { SalesFunnel } from "@/types/lead";

export function useSalesFunnels(workspaceId: string | null) {
  const [funnels, setFunnels] = useState<SalesFunnel[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFunnelId, setActiveFunnelId] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchFunnels = useCallback(async () => {
    if (!workspaceId) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("sales_funnels")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("order_index", { ascending: true });

      if (error) throw error;
      
      const typedData: SalesFunnel[] = (data || []).map(item => ({
        id: item.id,
        workspace_id: item.workspace_id,
        name: item.name,
        description: item.description,
        color: item.color || 'bg-blue-500',
        is_default: item.is_default || false,
        order_index: item.order_index || 0,
        created_at: item.created_at,
        updated_at: item.updated_at,
        integration_id: (item as any).integration_id || null,
      }));
      
      setFunnels(typedData);

      // Set default funnel as active if not set, OR reset if current active is not in the allowed list
      // (membros com restrições de acesso por funil podem ter um activeFunnelId fora da lista permitida)
      const currentIsAllowed = activeFunnelId
        ? typedData.some(f => f.id === activeFunnelId)
        : false;
      if (!currentIsAllowed && typedData.length > 0) {
        const defaultFunnel = typedData.find(f => f.is_default) || typedData[0];
        setActiveFunnelId(defaultFunnel.id);
      } else if (typedData.length === 0 && activeFunnelId) {
        setActiveFunnelId(null);
      }
    } catch (error: any) {
      console.error("Error fetching funnels:", error);
      toast({
        title: "Erro ao carregar funis",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [workspaceId, activeFunnelId, toast]);

  useEffect(() => {
    fetchFunnels();
  }, [fetchFunnels]);

  // Realtime subscription
  useEffect(() => {
    if (!workspaceId) return;

    const channelName = `sales-funnels-${workspaceId}`;
    let channel: RealtimeChannel | null = null;
    const reconcileTimerRef = { current: null as ReturnType<typeof setTimeout> | null };

    const subscribeChannel = () => {
      if (REALTIME_PAUSE_ON_HIDDEN_ENABLED && typeof document !== "undefined" && document.hidden) {
        return;
      }
      if (channel) return;
      channel = subscribePostgresChannel(
        channelName,
        {
          event: "*",
          schema: "public",
          table: "sales_funnels",
          filter: `workspace_id=eq.${workspaceId}`,
        },
        () => {
          fetchFunnels();
        },
      );
    };

    const teardownChannel = () => {
      if (!channel) return;
      removeSupabaseChannelByName(channelName);
      supabase.removeChannel(channel);
      channel = null;
    };

    const reconcileFunnels = () => {
      if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
      reconcileTimerRef.current = setTimeout(() => {
        fetchFunnels();
      }, 300);
    };

    const unbind = bindRealtimeVisibilityPause({
      pauseEnabled: REALTIME_PAUSE_ON_HIDDEN_ENABLED,
      subscribe: subscribeChannel,
      teardown: teardownChannel,
      onReconcile: reconcileFunnels,
    });

    return () => {
      unbind();
      if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
    };
  }, [workspaceId, fetchFunnels]);

  const createFunnel = async (data: { name: string; description?: string; color?: string }) => {
    if (!workspaceId) return null;

    try {
      const { data: newFunnel, error } = await supabase.rpc("create_sales_funnel", {
        p_workspace_id: workspaceId,
        p_name: data.name,
        p_description: data.description || null,
        p_color: data.color || "bg-blue-500",
      });

      if (error) throw error;

      await fetchFunnels();

      toast({
        title: "Funil criado",
        description: `O funil "${data.name}" foi criado com sucesso.`,
      });

      return newFunnel;
    } catch (error: any) {
      toast({
        title: "Erro ao criar funil",
        description: error.message,
        variant: "destructive",
      });
      return null;
    }
  };

  const updateFunnel = async (id: string, data: Partial<SalesFunnel>) => {
    try {
      const { error } = await supabase
        .from("sales_funnels")
        .update(data)
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Funil atualizado",
        description: "As alterações foram salvas com sucesso.",
      });

      return true;
    } catch (error: any) {
      toast({
        title: "Erro ao atualizar funil",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteFunnel = async (id: string) => {
    const funnel = funnels.find(f => f.id === id);
    if (!funnel) return false;

    const blockReason = getFunnelDeletionBlockReason(funnel, { funnelCount: funnels.length });
    if (blockReason) {
      toast({
        title: "Não é possível excluir",
        description: blockReason,
        variant: "destructive",
      });
      return false;
    }

    try {
      const { error } = await supabase
        .from("sales_funnels")
        .delete()
        .eq("id", id);

      if (error) throw error;

      const warning = getFunnelDeletionWarning(funnel, { funnelCount: funnels.length });
      toast({
        title: "Funil excluído",
        description: warning ?? "O funil foi removido com sucesso.",
      });

      // If deleted funnel was active, switch to default
      if (activeFunnelId === id) {
        const defaultFunnel = funnels.find(f => f.is_default && f.id !== id);
        setActiveFunnelId(defaultFunnel?.id || null);
      }

      return true;
    } catch (error: any) {
      const message =
        error.message?.includes("cannot_delete_default_funnel")
          ? "O funil padrão não pode ser excluído. Defina outro funil como padrão antes."
          : error.message?.includes("cannot_delete_integration_funnel")
            ? "Este funil está vinculado a uma integração e não pode ser excluído."
            : error.message;

      toast({
        title: "Erro ao excluir funil",
        description: message,
        variant: "destructive",
      });
      return false;
    }
  };

  const reorderFunnels = async (orderedIds: string[]) => {
    const previous = funnels;
    const byId = new Map(funnels.map((f) => [f.id, f]));
    const reordered = orderedIds
      .map((id, index) => {
        const funnel = byId.get(id);
        return funnel ? { ...funnel, order_index: index } : null;
      })
      .filter((f): f is SalesFunnel => f !== null);

    if (reordered.length !== orderedIds.length) {
      return false;
    }

    setFunnels(reordered);

    try {
      for (let index = 0; index < orderedIds.length; index++) {
        const { error } = await supabase
          .from("sales_funnels")
          .update({ order_index: index })
          .eq("id", orderedIds[index]);

        if (error) throw error;
      }

      toast({
        title: "Ordem dos funis atualizada",
      });

      return true;
    } catch (error: any) {
      setFunnels(previous);
      toast({
        title: "Erro ao reordenar funis",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const setDefaultFunnel = async (id: string) => {
    try {
      const { error } = await supabase
        .from("sales_funnels")
        .update({ is_default: true })
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Funil padrão atualizado",
        description: "Este funil agora é o padrão.",
      });

      return true;
    } catch (error: any) {
      toast({
        title: "Erro ao definir funil padrão",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const activeFunnel = funnels.find(f => f.id === activeFunnelId);

  return {
    funnels,
    loading,
    activeFunnelId,
    activeFunnel,
    setActiveFunnelId,
    createFunnel,
    updateFunnel,
    deleteFunnel,
    setDefaultFunnel,
    reorderFunnels,
    refetch: fetchFunnels,
  };
}
